import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  createSubmission,
  getForm,
  listForms,
  listSubmissions,
  sendFormToDisplay,
  type CustomForm,
  type FormField,
  type FormSubmission,
} from '@/lib/api/forms.js';
import { getDisplayConnectionStatus } from '@/lib/api/display-staff.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatDateTime } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { SignaturePad } from '@/components/common/SignaturePad.js';
import {
  buildFormSignatureValue,
  getSignatureDataUrl,
  getSignatureSignedAt,
  isSignatureFieldEmpty,
} from '@/lib/forms/signature.js';
import { useAuthStore } from '@/stores/auth.js';
import { DISPLAY_STAFF_WS_EVENT } from '@/hooks/useDisplayStaffBridge.js';

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: FormField;
  value: unknown;
  onChange: (val: unknown) => void;
}) {
  if (field.type === 'heading') {
    return <p className="text-sm font-bold text-slate-950">{field.label}</p>;
  }
  if (field.type === 'divider') {
    return <hr className="border-slate-200" />;
  }

  const label = (
    <span className="text-sm font-medium text-slate-700">
      {field.label}
      {field.required ? <span className="text-red-500"> *</span> : null}
    </span>
  );

  switch (field.type) {
    case 'textarea':
      return (
        <label className="block">
          {label}
          <textarea
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            rows={3}
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
          />
          {field.helpText ? <p className="mt-1 text-xs text-slate-500">{field.helpText}</p> : null}
        </label>
      );
    case 'number':
    case 'rating':
      return (
        <label className="block">
          {label}
          <input
            type="number"
            min={field.min}
            max={field.max}
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={value === undefined || value === null ? '' : String(value)}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          />
          {field.helpText ? <p className="mt-1 text-xs text-slate-500">{field.helpText}</p> : null}
        </label>
      );
    case 'select':
      return (
        <label className="block">
          {label}
          <select
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select…</option>
            {(field.options ?? []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>
      );
    case 'radio':
      return (
        <fieldset>
          {label}
          <div className="mt-2 space-y-1">
            {(field.options ?? []).map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name={field.id}
                  checked={value === opt}
                  onChange={() => onChange(opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        </fieldset>
      );
    case 'checkbox':
      return (
        <fieldset>
          {label}
          <div className="mt-2 space-y-1">
            {(field.options ?? []).map((opt) => {
              const arr = Array.isArray(value) ? (value as string[]) : [];
              return (
                <label key={opt} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={arr.includes(opt)}
                    onChange={(e) => {
                      if (e.target.checked) onChange([...arr, opt]);
                      else onChange(arr.filter((v) => v !== opt));
                    }}
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        </fieldset>
      );
    case 'yes_no':
      return (
        <fieldset>
          {label}
          <div className="mt-2 flex gap-4 text-sm">
            {(['yes', 'no'] as const).map((opt) => (
              <label key={opt} className="flex items-center gap-2 capitalize">
                <input
                  type="radio"
                  name={field.id}
                  checked={value === opt}
                  onChange={() => onChange(opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        </fieldset>
      );
    case 'date':
      return (
        <label className="block">
          {label}
          <input
            type="date"
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
          />
        </label>
      );
    case 'signature':
      return (
        <div className="block">
          {label}
          <div className="mt-1">
            <SignaturePad
              height={140}
              helpText={field.helpText ?? 'Sign above'}
              onChange={(dataUrl) => {
                if (!dataUrl) {
                  onChange(null);
                  return;
                }
                onChange(buildFormSignatureValue({ dataUrl }));
              }}
            />
          </div>
        </div>
      );
    default:
      return (
        <label className="block">
          {label}
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            placeholder={field.placeholder}
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
          />
          {field.helpText ? <p className="mt-1 text-xs text-slate-500">{field.helpText}</p> : null}
        </label>
      );
  }
}

function SubmissionFieldValue({ field, value }: { field?: FormField; value: unknown }) {
  if (field?.type === 'signature' || getSignatureDataUrl(value)) {
    const url = getSignatureDataUrl(value);
    const signedAt = getSignatureSignedAt(value);
    if (!url) return <span className="text-slate-400">—</span>;
    return (
      <div className="print:break-inside-avoid">
        <img
          src={url}
          alt={field?.label ?? 'Signature'}
          className="max-h-28 max-w-full rounded-lg border border-slate-200 bg-white object-contain print:max-h-40"
        />
        {signedAt ? (
          <p className="mt-1 text-xs text-slate-500">Signed {formatDateTime(signedAt)}</p>
        ) : null}
      </div>
    );
  }
  if (Array.isArray(value)) return <span>{value.join(', ')}</span>;
  if (value === null || value === undefined || value === '') {
    return <span className="text-slate-400">—</span>;
  }
  if (typeof value === 'object') {
    return <span className="text-xs text-slate-500">{JSON.stringify(value)}</span>;
  }
  return <span>{String(value)}</span>;
}

function SubmissionDetail({
  submission,
}: {
  submission: FormSubmission;
}) {
  const formQ = useQuery({
    queryKey: ['custom-form', submission.formId],
    queryFn: () => getForm(submission.formId),
  });
  const fields = formQ.data?.fields ?? [];
  const byId = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields]);
  const entries = Object.entries(submission.fieldValues ?? {});
  // Prefer form field order
  const orderedIds = fields.length
    ? fields
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((f) => f.id)
        .filter((id) => id in (submission.fieldValues ?? {}))
    : entries.map(([id]) => id);

  for (const [id] of entries) {
    if (!orderedIds.includes(id)) orderedIds.push(id);
  }

  return (
    <div className="mt-2 space-y-2 border-t border-slate-200 pt-2">
      {orderedIds.map((fieldId) => {
        const field = byId.get(fieldId);
        const val = submission.fieldValues[fieldId];
        if (field?.type === 'heading' || field?.type === 'divider') return null;
        return (
          <div key={fieldId} className="text-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {field?.label ?? fieldId}
            </p>
            <SubmissionFieldValue field={field} value={val} />
          </div>
        );
      })}
      {!orderedIds.length ? <p className="text-xs text-slate-500">No answers recorded.</p> : null}
    </div>
  );
}

export function TicketFormsSection({
  ticketId,
  customerId,
}: {
  ticketId: string;
  customerId?: string | null;
}) {
  const queryClient = useQueryClient();
  const storeId = useAuthStore((s) => s.user?.storeId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeForm, setActiveForm] = useState<CustomForm | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [waitingDisplay, setWaitingDisplay] = useState(false);

  const submissionsQ = useQuery({
    queryKey: ['form-submissions', ticketId],
    queryFn: () => listSubmissions({ ticketId, pageSize: 50 }),
  });

  const manualFormsQ = useQuery({
    queryKey: ['custom-forms-manual'],
    queryFn: () => listForms({ triggerType: 'manual', active: true }),
    enabled: pickerOpen,
  });

  const formDetailQ = useQuery({
    queryKey: ['custom-form', activeForm?.id],
    queryFn: () => getForm(activeForm!.id),
    enabled: Boolean(activeForm?.id),
  });

  const formFields = useMemo(
    () => [...(formDetailQ.data?.fields ?? activeForm?.fields ?? [])].sort((a, b) => a.order - b.order),
    [formDetailQ.data?.fields, activeForm?.fields],
  );

  const formMeta = formDetailQ.data ?? activeForm;

  const submitMut = useMutation({
    mutationFn: () => {
      for (const field of formFields) {
        if (field.required && field.type === 'signature' && isSignatureFieldEmpty(values[field.id])) {
          throw new Error(`Please sign "${field.label}" before submitting`);
        }
        if (
          field.required &&
          field.type !== 'heading' &&
          field.type !== 'divider' &&
          field.type !== 'signature' &&
          (values[field.id] === undefined || values[field.id] === null || values[field.id] === '')
        ) {
          throw new Error(`Field "${field.label}" is required`);
        }
      }
      const primary = formFields.find((f) => f.type === 'signature');
      const primarySig = primary ? getSignatureDataUrl(values[primary.id]) : null;
      return createSubmission(activeForm!.id, {
        ticketId,
        customerId: customerId ?? undefined,
        fieldValues: values,
        signatureDataUrl: primarySig ?? undefined,
        submittedVia: 'staff',
      });
    },
    onSuccess: () => {
      toastSuccess('Form submitted');
      setActiveForm(null);
      setValues({});
      setPickerOpen(false);
      queryClient.invalidateQueries({ queryKey: ['form-submissions', ticketId] });
    },
    onError: (e) => {
      if (e instanceof Error && !('response' in e)) {
        toastError(e.message);
        return;
      }
      toastError(parseApiError(e).message);
    },
  });

  const sendDisplayMut = useMutation({
    mutationFn: async () => {
      if (!storeId) throw new Error('No store selected');
      const status = await getDisplayConnectionStatus(storeId);
      if (!status.connected) {
        throw new Error('Customer display is not connected. Collect the form on this device instead.');
      }
      return sendFormToDisplay(activeForm!.id, {
        ticketId,
        customerId: customerId ?? undefined,
      });
    },
    onSuccess: () => {
      setWaitingDisplay(true);
      toastSuccess('Form sent to customer display…');
    },
    onError: (e) => {
      if (e instanceof Error && !('response' in e)) {
        toastError(e.message);
        return;
      }
      toastError(parseApiError(e).message);
    },
  });

  useEffect(() => {
    if (!waitingDisplay || !activeForm) return;
    const handler = (ev: Event) => {
      const msg = (ev as CustomEvent<{ type?: string; payload?: { formId?: string; ticketId?: string } }>)
        .detail;
      if (msg?.type !== 'display.form_submitted') return;
      if (msg.payload?.formId !== activeForm.id) return;
      if (msg.payload?.ticketId && msg.payload.ticketId !== ticketId) return;
      setWaitingDisplay(false);
      toastSuccess('Customer completed the form on the display');
      setActiveForm(null);
      setValues({});
      queryClient.invalidateQueries({ queryKey: ['form-submissions', ticketId] });
    };
    window.addEventListener(DISPLAY_STAFF_WS_EVENT, handler as EventListener);
    return () => window.removeEventListener(DISPLAY_STAFF_WS_EVENT, handler as EventListener);
  }, [waitingDisplay, activeForm, ticketId, queryClient]);

  function openForm(form: CustomForm) {
    setActiveForm(form);
    setValues({});
    setPickerOpen(false);
    setWaitingDisplay(false);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    submitMut.mutate();
  }

  const submissions = submissionsQ.data?.data ?? [];
  const canSendDisplay = Boolean(formMeta?.sendToDisplay);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm print:border-0 print:shadow-none">
      <div className="flex items-center justify-between gap-2 print:hidden">
        <h3 className="text-sm font-bold text-slate-950">Forms</h3>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          onClick={() => setPickerOpen(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          Add Form
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {submissions.map((s) => (
          <div key={s.id} className="rounded-xl bg-slate-50 px-3 py-2 text-sm print:bg-white print:px-0">
            <div className="w-full text-left">
              <p className="font-semibold text-slate-800">{s.formName ?? 'Form'}</p>
              <p className="text-xs text-slate-500">
                {s.submittedAt ? formatDateTime(s.submittedAt) : 'Submitted'}
                {s.submittedVia === 'customer_display' ? ' · Customer display' : ''}
              </p>
            </div>
            <SubmissionDetail submission={s} />
          </div>
        ))}
        {!submissions.length && !submissionsQ.isLoading ? (
          <p className="text-sm text-slate-500 print:hidden">No form submissions yet.</p>
        ) : null}
      </div>

      {pickerOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:hidden">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <h4 className="text-lg font-bold text-slate-950">Choose a form</h4>
            <div className="mt-3 max-h-64 space-y-2 overflow-y-auto">
              {(manualFormsQ.data ?? []).map((form) => (
                <button
                  key={form.id}
                  type="button"
                  className="block w-full rounded-xl border border-slate-200 px-4 py-3 text-left hover:border-primary"
                  onClick={() => openForm(form)}
                >
                  <p className="font-semibold text-slate-950">{form.name}</p>
                  {form.description ? (
                    <p className="text-xs text-slate-500">{form.description}</p>
                  ) : null}
                  {form.sendToDisplay ? (
                    <p className="mt-1 text-xs font-semibold text-primary">Customer display supported</p>
                  ) : null}
                </button>
              ))}
              {!manualFormsQ.data?.length && !manualFormsQ.isLoading ? (
                <p className="text-sm text-slate-500">No manual forms available.</p>
              ) : null}
            </div>
            <button
              type="button"
              className="mt-4 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
              onClick={() => setPickerOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {activeForm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:hidden">
          <form
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
            onSubmit={handleSubmit}
          >
            <h4 className="text-lg font-bold text-slate-950">{activeForm.name}</h4>
            {waitingDisplay ? (
              <div className="mt-6 flex flex-col items-center gap-3 py-8 text-center">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <p className="text-sm font-semibold text-slate-800">
                  Waiting for customer to complete the form on the display…
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                {formFields.map((field) => (
                  <FieldInput
                    key={field.id}
                    field={field}
                    value={values[field.id]}
                    onChange={(val) => setValues((prev) => ({ ...prev, [field.id]: val }))}
                  />
                ))}
              </div>
            )}
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => {
                  setActiveForm(null);
                  setValues({});
                  setWaitingDisplay(false);
                }}
              >
                Cancel
              </button>
              {canSendDisplay && !waitingDisplay ? (
                <button
                  type="button"
                  className="rounded-xl border border-primary px-4 py-2 text-sm font-semibold text-primary disabled:opacity-50"
                  disabled={sendDisplayMut.isPending}
                  onClick={() => sendDisplayMut.mutate()}
                >
                  {sendDisplayMut.isPending ? 'Sending…' : 'Send to customer display'}
                </button>
              ) : null}
              {!waitingDisplay ? (
                <button
                  type="submit"
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={submitMut.isPending}
                >
                  {submitMut.isPending ? 'Submitting…' : 'Submit'}
                </button>
              ) : null}
            </div>
          </form>
        </div>
      ) : null}
    </section>
  );
}
